import {
    Button,
    DialogTrigger,
    makeStyles,
    MenuButton,
    mergeClasses,
    Spinner,
    tokens,
    useRestoreFocusTarget,
} from '@fluentui/react-components';
import { FolderAdd20Regular } from '@fluentui/react-icons';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteTaxReturnFolderRecord, useGetTaxReturns } from '../../../api/report/report';
import { IFilterItem } from '../../../data-types/filterItem';
import { useFeedbackMessages } from '../../../hooks/useFeedbackMessages';
import { useFilterPickerState } from '../../../hooks/useFilterPickerState';
import { useFunctionalAccess } from '../../../hooks/useFunctionalAccess';
import { customPalette } from '../../../theme';
import { focusContextualMenuSearchBox } from '../../../utils/ContextualMenuUtils';
import { getFilterMenuAriaLabel } from '../../../utils/FilterUtils';
import ContextualMenuClearSelectionItem from '../../common/ContextualMenuClearSelectionItem';
import ContextualMenuFolderItem from '../../common/ContextualMenuFolderItem';
import FunctionalAccessDisableWrapper from '../../common/FunctionalAccessDisableWrapper';
import ConfirmationDialogV9 from '../../dialog/ConfirmationDialogV9';
import FilterPicker, { useFilterPickerStyles } from '../../filters/filter-picker/FilterPicker';
import FilterSearchBar from '../../filters/filter-search-bar/FilterSearchBar';

export interface IFoldersListFilterProps {
    folders: IFilterItem[];
    label?: string;
    filterName: string;
    showSingleSelect?: boolean;
    selectedFolderFilter?: IFilterItem | null;
    isFiltered: boolean;
    onButtonClick?: (filterName: string) => void;
    onFolderChange: (filteredItems: IFilterItem[], filterName: string) => void;
    updateFolderList?: () => Promise<any>;
    disabled?: boolean;
}

const useStyles = makeStyles({
    titleClass: {
        color: customPalette.blueDark,
        fontSize: '9px',
        fontStyle: 'normal',
        fontWeight: '500',
        lineHeight: '12px',
        letterSpacing: '2.4px',
        textTransform: 'uppercase',
        padding: '0 0 0 9px',
        height: '12px',
        display: 'block',
    },
    separator: {
        border: 'none',
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        margin: '6px 0',
        height: 0,
    },
    addFolderRow: {
        display: 'flex',
        width: '100%',
    },
    addFolderButton: {
        width: '100%',
        border: 0,
        backgroundColor: 'transparent',
        justifyContent: 'flex-start',
        boxShadow: 'none',
    },
    root: {
        display: 'flex',
        flexDirection: 'row',
    },
    column: {
        display: 'flex',
        flexDirection: 'column',
    },
});

const FoldersListFilter: React.FC<IFoldersListFilterProps> = ({
    folders,
    label,
    filterName,
    selectedFolderFilter,
    isFiltered,
    disabled,
    onFolderChange,
    updateFolderList,
}) => {
    const { t } = useTranslation();
    const styles = useStyles();
    const pickerStyles = useFilterPickerStyles();
    const { functionalAccess } = useFunctionalAccess();
    const [open, setOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [isAddingFolder, setIsAddingFolder] = useState(false);
    const [announceMessage, setAnnounceMessage] = useState<string | undefined>(undefined);
    const [folderPendingDelete, setFolderPendingDelete] = useState<IFilterItem | null>(null);
    const { displayFeedbackMessage } = useFeedbackMessages();
    const { mutateAsync: deleteFolderCall, isLoading: deleteFolderIsLoading } = useDeleteTaxReturnFolderRecord();
    const { mutateAsync: getTaxReturns } = useGetTaxReturns();
    const restoreFocusTargetAttributes = useRestoreFocusTarget();

    const selectedFolderUpdatedLabel = useMemo(
        () => folders.find((folder) => folder.key === selectedFolderFilter?.key)?.label,
        [folders, selectedFolderFilter]
    );
    const selectedFolderAsArray = useMemo(() => (selectedFolderFilter ? [selectedFolderFilter] : []), [selectedFolderFilter]);
    const isActiveSelectedFolder = selectedFolderFilter?.key !== undefined;

    const picker = useFilterPickerState({
        items: folders,
        committedSelection: selectedFolderAsArray,
        filterName,
        isFiltered,
        noResultsText: t('noFoldersFound'),
        searchItemName: 'folder',
        searchValue,
        setSearchValue,
        onCommit: onFolderChange,
        overrideAnnouncedMessage: announceMessage,
    });

    const resetOnClose = () => {
        setSearchValue('');
        setIsAddingFolder(false);
        setAnnounceMessage(undefined);
        picker.resetDraft();
    };

    const closePicker = () => {
        setOpen(false);
        resetOnClose();
    };

    const handleAddFolder = () => {
        if (isAddingFolder) {
            return;
        }
        setIsAddingFolder(true);
        setAnnounceMessage('');
    };

    const onCancelAddFolder = () => {
        setIsAddingFolder(false);
        setAnnounceMessage(undefined);
    };

    const onSaveFolderCallback = () => {
        setAnnounceMessage('');
        setIsAddingFolder(false);
    };

    const cancelDeleteFolder = () => {
        setFolderPendingDelete(null);
    };

    const handleDeleteFolder = () => {
        if (!folderPendingDelete) {
            return;
        }

        const folderKey = folderPendingDelete.key;
        const isSelectedFolder = selectedFolderFilter?.key === folderPendingDelete.key;

        deleteFolderCall({ data: { folderKey: parseInt(folderKey, 10) } }).then(() => {
            setFolderPendingDelete(null);
            if (isSelectedFolder) {
                onFolderChange([], '');
                getTaxReturns({ data: {} });
            }
            updateFolderList?.().then(() => {
                focusContextualMenuSearchBox();
                displayFeedbackMessage({ message: t('folderDeleted').toString(), type: 'success' });
            });
        });
    };

    const ariaLabelForButton = useMemo(() => {
        if (isFiltered) {
            return `${t('selectFolder')}. ${t('currentlySelectedFolder')} ${selectedFolderUpdatedLabel}.`;
        }
        return `${t('selectFolder')}. ${t('currentlySelected')}${t('allCalculations')}.`;
    }, [isFiltered, t, selectedFolderUpdatedLabel]);

    const buttonId = `${filterName}-button`;
    const filterMenuAriaLabel = getFilterMenuAriaLabel('selectFolder', t);
    const triggerLabel = t(selectedFolderUpdatedLabel || label || filterName);

    const listContent =
        !picker.hasResults && !isAddingFolder ? (
            <div className={pickerStyles.noResults}>{t('noFoldersFound')}</div>
        ) : (
            <>
                {isAddingFolder ? (
                    <ContextualMenuFolderItem
                        isNewFolder
                        itemIndex={0}
                        dismissMenu={closePicker}
                        updateFolderList={updateFolderList}
                        onFolderChange={onFolderChange}
                        onCancel={onCancelAddFolder}
                        onSaveFolder={onSaveFolderCallback}
                        filterName={filterName}
                    />
                ) : null}
                {picker.visibleItems.map((folder, index) => (
                    <ContextualMenuFolderItem
                        key={folder.key}
                        folder={folder}
                        isSelected={selectedFolderFilter?.key === folder.key}
                        itemIndex={isAddingFolder ? index + 1 : index}
                        dismissMenu={closePicker}
                        updateFolderList={updateFolderList}
                        onFolderChange={onFolderChange}
                        filterName={filterName}
                        onDeleteRequest={setFolderPendingDelete}
                    />
                ))}
            </>
        );

    return (
        <div className={styles.root}>
            <div className={styles.column}>
                <label className={styles.titleClass}>{isFiltered ? t('selectedFolder') : t('selectFolder')}</label>
                <FilterPicker
                    open={open}
                    trapFocus={folderPendingDelete === null}
                    contentRootProps={restoreFocusTargetAttributes as React.ComponentPropsWithoutRef<'div'>}
                    onOpenChange={(next) => {
                        if (!next && folderPendingDelete) {
                            return;
                        }
                        setOpen(next);
                        if (!next) {
                            resetOnClose();
                        }
                    }}
                    surfaceAriaLabel={filterMenuAriaLabel}
                    contentAriaLabel={filterMenuAriaLabel}
                    trigger={
                        <MenuButton
                            id={buttonId}
                            aria-label={ariaLabelForButton}
                            aria-expanded={open}
                            aria-haspopup='menu'
                            disabled={disabled}
                            disabledFocusable={disabled}
                            appearance='secondary'
                            className={mergeClasses(isActiveSelectedFolder && pickerStyles.activeTrigger, open && pickerStyles.openTrigger)}
                            data-active={isActiveSelectedFolder ? 'true' : 'false'}
                        >
                            {triggerLabel}
                        </MenuButton>
                    }
                    announcementMessage={picker.announcementMessage}
                    search={
                        <FilterSearchBar
                            searchBoxPlaceholderText={t('search')}
                            onSearchBoxChange={(_ev, newValue) => picker.onSearchChange(newValue)}
                            onAbort={picker.onAbortSearch}
                        />
                    }
                    clearSelection={
                        picker.showClearAll ? (
                            <ContextualMenuClearSelectionItem
                                text={t('viewAll')}
                                dismissMenu={closePicker}
                                onViewAllClick={picker.clearAll}
                            />
                        ) : undefined
                    }
                    list={listContent}
                    footer={
                        <div key='folderActionsBar'>
                            <hr className={styles.separator} />
                            <div className={styles.addFolderRow}>
                                <FunctionalAccessDisableWrapper hasFunctionalAccess={functionalAccess?.createFolderButtonEnabled}>
                                    <Button
                                        aria-label={t('newFolder').toString()}
                                        icon={<FolderAdd20Regular />}
                                        className={styles.addFolderButton}
                                        onClick={handleAddFolder}
                                    >
                                        {t('newFolder').toString()}
                                    </Button>
                                </FunctionalAccessDisableWrapper>
                            </div>
                        </div>
                    }
                />
            </div>
            {/* Sibling alert dialog — nested Dialog inside trapFocus Popover is aria-hidden by Tabster; release trapFocus while pending delete. */}
            {folderPendingDelete !== null && (
                <ConfirmationDialogV9
                    modalType='alert'
                    onDismiss={cancelDeleteFolder}
                    title={t('deleteFolderTitle')}
                    content={t('deleteFolderSubTitle')}
                    buttons={
                        <>
                            <DialogTrigger>
                                <Button
                                    aria-label={t('cancel').toString()}
                                    onClick={cancelDeleteFolder}
                                    disabled={deleteFolderIsLoading}
                                    data-analytics-button-name='confirmation_dialog_cancel'
                                >
                                    {t('cancel').toString()}
                                </Button>
                            </DialogTrigger>
                            <Button
                                appearance='primary'
                                aria-label={t('delete').toString()}
                                onClick={handleDeleteFolder}
                                disabled={Boolean(deleteFolderIsLoading)}
                                data-analytics-button-name='confirmation_dialog_save'
                            >
                                {deleteFolderIsLoading ? t('deleting').toString() : t('delete').toString()}
                                {deleteFolderIsLoading && <Spinner size='extra-tiny' />}
                            </Button>
                        </>
                    }
                />
            )}
        </div>
    );
};

export default FoldersListFilter;
